import React from "react";
import { CustomIcon } from "./CustomIcon";
import { Button } from "./Button";

// ============================================================
// EmptyState — Phase 1 primitive for empty list / zero-data views.
// ============================================================
// Standard pattern: an elevated circular icon well, a concise
// muted headline, an optional supporting sentence, and an optional
// primary CTA button that jump-starts the user (e.g. "Add member",
// "Create appointment").
//
// Props:
//   icon     → CustomIcon name (string) OR a React node (any JSX).
//   title    → required headline (string).
//   subtitle → optional sub-headline (string), capped at 260px width.
//   action   → optional { label, onClick } for the primary CTA.
//   className → merged with the base wrapper class list.
// ============================================================

export function EmptyState({
  icon,
  title,
  subtitle,
  action,
  className = "",
  ...rest
}) {
  const combined = ["kc-empty-state", className].filter(Boolean).join(" ");
  return (
    <div
      className={combined}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        width: "100%",
        padding: "var(--space-xxl) var(--space-lg)",
        gap: "var(--space-md)",
      }}
      {...rest}
    >
      {icon !== undefined && icon !== null && (
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "var(--kc-elevated)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--kc-sub)",
            flexShrink: 0,
          }}
        >
          {typeof icon === "string" ? <CustomIcon name={icon} size={28} /> : icon}
        </div>
      )}
      <div className="text-section-header" style={{ color: "var(--kc-sub)" }}>
        {title}
      </div>
      {subtitle && (
        <div
          className="text-card-secondary"
          style={{ color: "var(--kc-meta)", maxWidth: 260 }}
        >
          {subtitle}
        </div>
      )}
      {action && action.label && (
        <Button variant="primary" onClick={action.onClick} data-testid="empty-state-action">
          {action.label}
        </Button>
      )}
    </div>
  );
}

export default EmptyState;
